import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, Spinner, Text, tokens } from '@fluentui/react-components';
import { CalendarClock24Regular, Edit24Regular, Key24Regular } from '@fluentui/react-icons';
import { useTopBarTitle, useTopBarBreadcrumbs, useTopBarActionItems } from '../../contexts/TopBarContext';
import type { TopBarAction } from '../../contexts/TopBarContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useWorkspaceEventsContext } from '../../contexts/WorkspaceEventsContext';
import { connectionsService } from '../../api/connectionsService';
import { connectorsService } from '../../api/connectorsService';
import type { Connection, Connector, SelectedEntity } from '../../api/types';
import UploadView from './UploadView';
import ProgressView from './ProgressView';
import AuthStep from './AuthStep';
import EntitySelection from './EntitySelection';
import PublicWorkbookSetup from './PublicWorkbookSetup';
import PostgreSQLSetup from './PostgreSQLSetup';
import ApplicationInsightsSetup from './ApplicationInsightsSetup';
import { ConnectionEditor, hasConnectionEditor } from './ConnectionEditor';
import { connectionEditBusy, supportsConnectionEditing } from './connectionEditing';
import { needsInitialEntitySelection } from './connectionSetupRouting';
import ConnectionScheduleEditor from './ConnectionScheduleEditor';
import { supportsScheduling } from './syncSchedule';
import './ConnectionPage.css';

// Non-progress screens register their actions here; ProgressView owns its own group.
const ConnectionHeaderActions: React.FC<{ actions: readonly TopBarAction[] }> = ({ actions }) => {
  useTopBarActionItems(actions);
  return null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConnectionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { connections, refreshConnections } = useConnections();
  const { dataModelCreated, clearDataModelCreated } = useWorkspaceEventsContext();

  const isNewConnection = !id;
  const rawConnectorId = searchParams.get('connector') || '';
  // 'file' is a virtual connector ID that unifies CSV + Excel uploads
  const isUnifiedFileUpload = rawConnectorId === 'file';
  const connectorId = rawConnectorId;
  const isPublicWorkbook = ['google_sheets', 'excel_online'].includes(connectorId);

  // ─── State for connector metadata ──────────────────────────────────
  const [connector, setConnector] = useState<Connector | null>(null);
  const [connectorLoading, setConnectorLoading] = useState(isNewConnection && !!connectorId);
  const [connectorError, setConnectorError] = useState<string | null>(null);

  // ─── State for draft connection (created for non-file-upload connectors) ──
  const [draftConnectionId, setDraftConnectionId] = useState<string | null>(null);
  const [draftCreating, setDraftCreating] = useState(false);

  // ─── State for existing connection details ─────────────────────────
  const [connectionDetail, setConnectionDetail] = useState<Connection | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(!!id);
  const [isEditingConnection, setIsEditingConnection] = useState(false);
  const [reauthorizing, setReauthorizing] = useState(false);
  const [reauthorizationError, setReauthorizationError] = useState<string | null>(null);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const scheduleActionRef = useRef<HTMLButtonElement>(null);

  // ─── State for "Edit Tables" mode ──────────────────────────────────
  const [isEditingTables, setIsEditingTables] = useState(false);
  const [editInitialEntities, setEditInitialEntities] = useState<SelectedEntity[] | undefined>(undefined);

  const editConnectionAction = useMemo<TopBarAction | null>(() => {
    if (isNewConnection || !connectionDetail || connectionLoading || isEditingConnection || isEditingTables
      || !supportsConnectionEditing(connectionDetail) || !hasConnectionEditor(connectionDetail.connectorId)) {
      return null;
    }
    return {
      id: 'edit-connection',
      label: 'Edit connection',
      appearance: 'outline',
      icon: <Edit24Regular />,
      disabled: connectionEditBusy(connectionDetail),
      onClick: () => setIsEditingConnection(true),
    };
  }, [isNewConnection, connectionDetail, connectionLoading, isEditingConnection, isEditingTables]);

  const reauthorizeAction = useMemo<TopBarAction | null>(() => {
    if (isNewConnection || !connectionDetail || connectionDetail.status === 'reauth_required'
      || !['oauth', 'oauth2'].includes(connectionDetail.connector.authMethod)) return null;
    return {
      id: 'reauthorize',
      label: reauthorizing ? 'Redirecting…' : 'Reauthorize',
      appearance: 'outline',
      icon: <Key24Regular />,
      disabled: reauthorizing || connectionEditBusy(connectionDetail),
      onClick: async () => {
        setReauthorizing(true);
        setReauthorizationError(null);
        try {
          const result = await connectionsService.initiateOAuth(connectionDetail.id);
          window.location.href = result.authorization_url;
        } catch (error) {
          setReauthorizationError(error instanceof Error ? error.message : 'Failed to start reauthorization');
          setReauthorizing(false);
        }
      },
    };
  }, [isNewConnection, connectionDetail, reauthorizing]);

  const scheduleAction = useMemo<TopBarAction | null>(() => {
    if (isNewConnection || !connectionDetail || !supportsScheduling(connectionDetail.connector.capabilities?.scheduled_sync)) return null;
    return {
      id: 'sync-schedule',
      label: 'Sync schedule',
      appearance: 'outline',
      icon: <CalendarClock24Regular />,
      disabled: connectionEditBusy(connectionDetail),
      triggerRef: scheduleActionRef,
      onClick: () => setIsEditingSchedule(true),
    };
  }, [isNewConnection, connectionDetail]);

  const closeScheduleDrawer = useCallback(() => {
    setIsEditingSchedule(false);
    window.setTimeout(() => scheduleActionRef.current?.focus(), 0);
  }, []);

  const connectionActions = useMemo<readonly TopBarAction[]>(() => (
    [reauthorizeAction, editConnectionAction, scheduleAction].filter((action): action is TopBarAction => action !== null)
  ), [reauthorizeAction, editConnectionAction, scheduleAction]);

  // Derive label from connector or fallback
  const connectorLabel = connector?.name || connectorId.charAt(0).toUpperCase() + connectorId.slice(1);

  // Try to get the name from the connections list first (fast path)
  const listName = id ? connections.find((c) => c.id === id)?.name : undefined;

  // Resolve the connection name
  const connectionName = listName || connectionDetail?.name || 'Data Source';

  const pageTitle = isNewConnection
    ? `New ${connectorLabel} Data Source`
    : connectionName;

  const breadcrumbLabel = isNewConnection
    ? 'New Data Source'
    : connectionName;

  // Top bar title and breadcrumbs
  useTopBarTitle(pageTitle);
  useTopBarBreadcrumbs([
    { text: 'Data Sources', to: '/data-sources' },
    { text: breadcrumbLabel },
  ]);

  // ─── Auto-navigate to data model when auto-pipeline creates one ────
  useEffect(() => {
    if (!dataModelCreated) return;
    // Only navigate if the event is for THIS connection
    const currentConnectionId = id || draftConnectionId;
    if (dataModelCreated.connectionId === currentConnectionId) {
      clearDataModelCreated();
      navigate(`/data-models/${dataModelCreated.dataModelId}`, { replace: true });
    }
  }, [dataModelCreated, id, draftConnectionId, navigate, clearDataModelCreated]);

  // ─── Fetch connection details for existing connections ─────────────
  useEffect(() => {
    if (!id) return;

    const fetchConnection = async () => {
      try {
        setIsEditingConnection(false);
        setConnectionLoading(true);
        const conn = await connectionsService.getConnection(id);
        setConnectionDetail(conn);
      } catch (err) {
        setConnectorError(
          err instanceof Error ? err.message : 'Failed to load connection',
        );
      } finally {
        setConnectionLoading(false);
      }
    };

    fetchConnection();
  }, [id]);

  // ─── Fetch connector details on mount (new connection only) ────────
  useEffect(() => {
    if (!isNewConnection || !connectorId) return;

    // 'file' is a virtual connector that unifies CSV + Excel uploads
    if (isUnifiedFileUpload) {
      setConnector({
        id: 'file',
        name: 'File Upload',
        authMethod: 'file_upload',
      } as Connector);
      setConnectorLoading(false);
      return;
    }

    const fetchConnector = async () => {
      try {
        setConnectorLoading(true);
        setConnectorError(null);
        const data = await connectorsService.getConnectorById(connectorId);
        setConnector(data);
      } catch (err) {
        setConnectorError(
          err instanceof Error ? err.message : 'Failed to load connector details',
        );
      } finally {
        setConnectorLoading(false);
      }
    };

    fetchConnector();
  }, [isNewConnection, connectorId, isUnifiedFileUpload]);

  // ─── Create draft connection for non-file-upload connectors ────────
  useEffect(() => {
    if (!connector || !isNewConnection) return;
    if (connector.authMethod === 'file_upload') return;
    if (isPublicWorkbook || connectorId === 'postgresql' || connectorId === 'application_insights') return;
    if (draftConnectionId || draftCreating) return;

    const createDraft = async () => {
      setDraftCreating(true);
      try {
        const conn = await connectionsService.createConnection({
          connectorId: connector.id,
          name: connector.name,
        });
        setDraftConnectionId(conn.id);
      } catch (err) {
        setConnectorError(
          err instanceof Error ? err.message : 'Failed to create connection',
        );
      } finally {
        setDraftCreating(false);
      }
    };

    createDraft();
  }, [connector, isNewConnection, draftConnectionId, draftCreating, isPublicWorkbook, connectorId]);

  // ─── Handlers ──────────────────────────────────────────────────────

  const handleUploadComplete = useCallback(
    (connectionId: string) => {
      navigate(`/data-sources/${connectionId}`, { replace: true });
    },
    [navigate],
  );

  const handlePublicWorkbookComplete = useCallback(
    (connectionId: string) => {
      refreshConnections();
      navigate(`/data-sources/${connectionId}`, { replace: true });
    },
    [navigate, refreshConnections],
  );

  const handleAuthComplete = useCallback(() => {
    if (draftConnectionId) {
      refreshConnections();
      navigate(`/data-sources/${draftConnectionId}`, { replace: true });
    }
  }, [draftConnectionId, navigate, refreshConnections]);

  const handleReauthComplete = useCallback(() => {
    // After re-authentication, refresh the connection detail
    // The status should flip back to 'connected', showing entity selection
    if (id) {
      refreshConnections();
      connectionsService.getConnection(id).then((conn) => {
        setConnectionDetail(conn);
      }).catch((err) => {
        console.error('Failed to refresh connection after re-auth:', err);
      });
    }
  }, [id, refreshConnections]);

  const handleEntitySelectionComplete = useCallback(() => {
    // Refresh the connection detail to get updated status ('ready')
    if (id) {
      refreshConnections();
      connectionsService.getConnection(id).then((conn) => {
        setConnectionDetail(conn);
      }).catch((err) => {
        console.error('Failed to refresh connection after entity selection:', err);
      });
    }
  }, [id, refreshConnections]);

  const handleEditTablesCancel = useCallback(() => {
    // Unmount the editor to discard its local selection and stop discovery polling.
    setIsEditingTables(false);
    setEditInitialEntities(undefined);
  }, []);

  const handleEntitySelectionCancel = useCallback(() => {
    // Keep the connection; discard the unsaved table selection by leaving setup.
    navigate('/data-sources');
  }, [navigate]);

  const handleEditTablesComplete = useCallback(() => {
    // Exit edit mode and refresh connection detail
    setIsEditingTables(false);
    setEditInitialEntities(undefined);
    if (id) {
      refreshConnections();
      connectionsService.getConnection(id).then((conn) => {
        setConnectionDetail(conn);
      }).catch((err) => {
        console.error('Failed to refresh connection after editing tables:', err);
      });
    }
  }, [id, refreshConnections]);

  const handleEditTables = useCallback(async () => {
    if (!id) return;
    try {
      const result = await connectionsService.getSelectedEntities(id);
      setEditInitialEntities(result.entities);
      setIsEditingTables(true);
    } catch {
      // If we can't fetch entities, still open editor without preselection
      setEditInitialEntities(undefined);
      setIsEditingTables(true);
    }
  }, [id]);

  // ─── Determine what to render for existing connections ─────────────

  const renderExistingConnection = () => {
    if (connectionLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spinner size="large" label="Loading connection…" />
        </div>
      );
    }

    if (!connectionDetail) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
            Connection not found
          </Text>
        </div>
      );
    }

    if (isEditingConnection && supportsConnectionEditing(connectionDetail)) {
      return <ConnectionEditor
        key={connectionDetail.id}
        connection={connectionDetail}
        onCancel={() => setIsEditingConnection(false)}
        onSaved={saved => {
          setConnectionDetail(saved);
          setIsEditingConnection(false);
          refreshConnections();
        }}
      />;
    }

    // Token expired — show auth step to re-authenticate
    if (connectionDetail.status === 'reauth_required') {
      return (
        <AuthStep
          connectionId={id!}
          authMethod={connectionDetail.connector.authMethod}
          connectorId={connectionDetail.connectorId}
          connectorName={connectionDetail.connector.name}
          logoUrl={connectionDetail.connector.logoUrl}
          onComplete={handleReauthComplete}
          isReauthorization
        />
      );
    }

    // Verified secretless connectors remain draft until tables are selected.
    if (needsInitialEntitySelection(connectionDetail)) {
      return (
        <EntitySelection
          connectionId={id!}
          normalizedRetention={connectionDetail.connector.capabilities?.normalized_retention}
          autoSelectSingleEntity={connectionDetail.connector.capabilities?.auto_select_single_entity === true}
          onComplete={handleEntitySelectionComplete}
          onCancel={handleEntitySelectionCancel}
        />
      );
    }

    // For all other statuses (ready, syncing, done, error, etc.) — show progress
    // If editing tables, show EntitySelection instead of ProgressView
    if (isEditingTables) {
      return (
        <EntitySelection
          connectionId={id!}
          normalizedRetention={connectionDetail.connector.capabilities?.normalized_retention}
          initialSelected={editInitialEntities}
          onComplete={handleEditTablesComplete}
          onCancel={handleEditTablesCancel}
        />
      );
    }

    return <ProgressView connectionId={id!} connectorId={connectionDetail.connectorId} onEditTables={handleEditTables} onReconnect={handleReauthComplete} additionalTopBarActions={connectionActions} />;
  };

  // ─── Render ────────────────────────────────────────────────────────

  // Loading state
  if (isNewConnection && (connectorLoading || draftCreating)) {
    return (
      <div className="connection-page">
        <div className="connection-page-content" style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spinner size="large" label={draftCreating ? 'Setting up connection…' : 'Loading connector…'} />
        </div>
      </div>
    );
  }

  // Error state
  if (connectorError) {
    return (
      <div className="connection-page">
        <div className="connection-page-content" style={{ padding: 40, textAlign: 'center' }}>
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
            {connectorError}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="connection-page">
      {reauthorizationError && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{reauthorizationError}</MessageBarBody>
        </MessageBar>
      )}
      <div className="connection-page-content">
        {connectionActions.length > 0 && connectionDetail && (connectionDetail.status === 'reauth_required' || needsInitialEntitySelection(connectionDetail)) && (
          <ConnectionHeaderActions actions={connectionActions} />
        )}
        {isNewConnection ? (
          // ── New connection: branch on auth method ──
          connector?.authMethod === 'file_upload' ? (
            <UploadView onUploadComplete={handleUploadComplete} />
          ) : connectorId === 'postgresql' ? (
            <PostgreSQLSetup connector={connector ?? undefined} onComplete={handlePublicWorkbookComplete} />
          ) : connectorId === 'application_insights' ? (
            <ApplicationInsightsSetup onComplete={handlePublicWorkbookComplete} />
          ) : connector && isPublicWorkbook ? (
            <PublicWorkbookSetup
              connector={connector}
              onComplete={handlePublicWorkbookComplete}
            />
          ) : draftConnectionId && connector ? (
            <AuthStep
              connectionId={draftConnectionId}
              authMethod={connector.authMethod}
              connectorId={connectorId}
              connectorName={connector.name}
              logoUrl={connector.logoUrl}
              onComplete={handleAuthComplete}
            />
          ) : null
        ) : (
          // ── Existing connection: status-based routing ──
          renderExistingConnection()
        )}
      </div>
      {connectionDetail && supportsScheduling(connectionDetail.connector.capabilities?.scheduled_sync) && (
        <ConnectionScheduleEditor
          connection={connectionDetail}
          open={isEditingSchedule}
          onClose={closeScheduleDrawer}
          onSaved={saved => {
            setConnectionDetail(saved);
            refreshConnections();
            closeScheduleDrawer();
          }}
        />
      )}
    </div>
  );
};

export default ConnectionPage;
